// src/components/LeaderboardSection.jsx
import React, { useEffect, useState } from "react";
import { ref, onValue } from "firebase/database";
import { db } from "../firebase";
import { motion } from "framer-motion";
import { Trophy, Medal, Award, TrendingUp } from "lucide-react";

const truncateAddress = (address) =>
  `${address.slice(0, 6)}...${address.slice(-4)}`;

const LeaderboardSection = () => {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const usersRef = ref(db, "users");
    const unsubscribe = onValue(
      usersRef,
      (snapshot) => {
        const data = snapshot.val();
        if (!data) {
          setLeaders([]);
          setLoading(false);
          return;
        }
        const entries = Object.entries(data).map(([wallet, details = {}]) => ({
          wallet,
          gamesPlayed: Number(details.gamesPlayed) || 0,
          won: Number(details.won) || 0,
        }));
        entries.sort((a, b) => b.won - a.won);
        setLeaders(entries.slice(0, 20));
        setLoading(false);
      },
      (err) => {
        console.error("Leaderboard onValue error:", err);
        setLeaders([]);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const getRankIcon = (index) => {
    switch (index) {
      case 0:
        return <Trophy className="w-5 h-5 text-yellow-400" />;
      case 1:
        return <Medal className="w-5 h-5 text-gray-300" />;
      case 2:
        return <Award className="w-5 h-5 text-amber-600" />;
      default:
        return null;
    }
  };

  const getRankStyle = (index) => {
    switch (index) {
      case 0:
        return "border-yellow-500/50 bg-gradient-to-r from-yellow-500/10 to-transparent hover:border-yellow-500/70";
      case 1:
        return "border-gray-400/50 bg-gradient-to-r from-gray-400/10 to-transparent hover:border-gray-400/70";
      case 2:
        return "border-amber-600/50 bg-gradient-to-r from-amber-600/10 to-transparent hover:border-amber-600/70";
      default:
        return "border-white/10 hover:border-white/30";
    }
  };

  return (
    <section
      id="leaderboard"
      className="relative min-h-screen bg-gradient-to-b from-[#190019] via-[#370c2e] to-[#0e101c] text-white py-24 px-4 flex flex-col items-center overflow-hidden"
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 z-0">
        <motion.div
          className="absolute top-20 right-20 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl"
          animate={{ y: [0, 50, 0], x: [0, -30, 0] }}
          transition={{ duration: 15, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-20 left-20 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"
          animate={{ y: [0, -50, 0], x: [0, 30, 0] }}
          transition={{ duration: 12, repeat: Infinity }}
        />
      </div>

      <div className="relative w-full max-w-5xl z-10 mt-24">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-6xl font-bold font-rubik drop-shadow-xl mb-4">
            Leaderboard
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-pink-500 to-purple-500 mx-auto rounded-full mb-4" />
          <p className="text-white/70 text-sm md:text-base">
            Top 20 players ranked by highest scores
          </p>
        </motion.div>

        {/* Coming Soon Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8"
        >
          <div className="relative overflow-hidden bg-gradient-to-r from-purple-500/10 to-pink-500/10 backdrop-blur-sm border border-purple-400/30 rounded-2xl p-6 shadow-xl">
            <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/20 to-pink-500/20 blur-xl opacity-50" />
            <div className="relative flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                <Trophy className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-2">Leaderboard Coming with Full Launch</h3>
                <p className="text-sm md:text-base text-white/80 leading-relaxed">
                  The leaderboard will be activated once the full game launches with competitive features.
                  Play now and familiarize yourself with the game mechanics.
                  Once competitive mode goes live, all your scores will be tracked here — compete for the top spots!
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Leaderboard Container */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="relative overflow-hidden bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 shadow-2xl"
        >
          {/* Gradient glow */}
          <div className="absolute -inset-1 bg-gradient-to-r from-pink-500/20 to-purple-500/20 blur-2xl opacity-50" />

          <div className="relative p-4 md:p-6">
            {/* Table Header */}
            <div className="grid grid-cols-[auto_1fr_auto_auto] gap-4 px-4 py-3 mb-4 bg-white/5 rounded-xl border border-white/10">
              <div className="text-xs md:text-sm font-semibold text-white/60 uppercase tracking-wider">Rank</div>
              <div className="text-xs md:text-sm font-semibold text-white/60 uppercase tracking-wider">Wallet</div>
              <div className="text-xs md:text-sm font-semibold text-white/60 uppercase tracking-wider text-right">Games</div>
              <div className="text-xs md:text-sm font-semibold text-white/60 uppercase tracking-wider text-right">Won</div>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-pink-500 rounded-full animate-pulse" />
                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                  <div className="w-2 h-2 bg-pink-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                </div>
              </div>
            )}

            {/* Empty State */}
            {!loading && leaders.length === 0 && (
              <div className="text-center py-12">
                <TrendingUp className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/60">No leaderboard data yet. Be the first to play!</p>
              </div>
            )}

            {/* Leader Rows */}
            <div className="space-y-2">
              {leaders.map((leader, index) => (
                <motion.div
                  key={leader.wallet}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className={`group grid grid-cols-[auto_1fr_auto_auto] gap-4 px-4 py-3 rounded-xl border transition-all duration-300 hover:scale-[1.02] hover:shadow-lg ${getRankStyle(index)}`}
                >
                  {/* Rank */}
                  <div className="flex items-center gap-2">
                    {getRankIcon(index)}
                    <span className={`font-bold text-sm md:text-base ${
                      index === 0 ? "text-yellow-400" :
                      index === 1 ? "text-gray-300" :
                      index === 2 ? "text-amber-600" :
                      "text-white/80"
                    }`}>
                      {index + 1}
                    </span>
                  </div>

                  {/* Wallet */}
                  <div className="flex items-center">
                    <code className="text-xs md:text-sm font-mono text-white/90 bg-black/20 px-2 py-1 rounded">
                      {truncateAddress(leader.wallet)}
                    </code>
                  </div>

                  {/* Games Played */}
                  <div className="flex items-center justify-end">
                    <span className="text-sm md:text-base text-white/70 font-medium">
                      {leader.gamesPlayed}
                    </span>
                  </div>

                  {/* Total Won */}
                  <div className="flex items-center justify-end">
                    <span className="text-sm md:text-base font-bold text-white">
                      {leader.won}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Live Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="flex items-center justify-center gap-2 mt-6"
        >
          <div className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />
          <span className="text-xs text-white/60 uppercase tracking-wider">Live Updates</span>
        </motion.div>
      </div>
    </section>
  );
};

export default LeaderboardSection;
